import { create } from 'zustand';

// How often THIS user opens each module — the global search suggests the five used most.
// Kept in this browser only (localStorage, one entry per user); nothing is sent to the server.
//
// Each module keeps { s: score, t: last visit }. The score decays with a 30-day half-life, so what the user opens
// NOW outranks something used heavily months ago.
const HALF_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
const keyOf = (userId) => `sohozkaj:module-usage:${userId}`;

const decayed = (entry, now) => (entry ? entry.s * Math.pow(0.5, (now - entry.t) / HALF_LIFE_MS) : 0);

function read(userId) {
    try { return JSON.parse(localStorage.getItem(keyOf(userId)) || '{}') || {}; } catch { return {}; }
}
function write(userId, data) {
    try { localStorage.setItem(keyOf(userId), JSON.stringify(data)); } catch { /* storage full or blocked */ }
}

export const useModuleUsageStore = create((set, get) => ({
    byUser: {},    // { [userId]: { [moduleId]: { s, t } } } — loaded from localStorage on first use

    /** Load a user's saved usage (once). */
    ensure: (userId) => {
        if (userId == null || get().byUser[userId]) return;
        set((st) => ({ byUser: { ...st.byUser, [userId]: read(userId) } }));
    },

    /** Count one visit to a module. */
    bump: (userId, moduleId) => {
        if (userId == null || !moduleId) return;
        const now = Date.now();
        const cur = get().byUser[userId] || read(userId);
        const next = { ...cur, [moduleId]: { s: decayed(cur[moduleId], now) + 1, t: now } };
        write(userId, next);
        set((st) => ({ byUser: { ...st.byUser, [userId]: next } }));
    },
}));

/** Module ids the user has opened, most used first (recent use counts for more). */
export function rankedModuleIds(usage, now = Date.now()) {
    return Object.entries(usage || {})
        .map(([id, entry]) => ({ id, score: decayed(entry, now), t: entry.t || 0 }))
        .filter((x) => x.score > 0.05)
        .sort((a, b) => b.score - a.score || b.t - a.t)
        .map((x) => x.id);
}
